import { useEffect } from "react";

/**
 * Describes one of a `SkyDialog`'s own footer buttons by an element on the screen (round 7, the
 * backlog row "Name the reason on the Run dialog's disabled Queue run button").
 *
 * skypoint-ui's `DialogActionConfig` takes a label, a handler and flags, and no description, so a
 * disabled primary action cannot say why through its props. The library stamps
 * `<dialog testId>-primary` (and `-secondary`, `-tertiary`) on each footer button; this sets that
 * button's `aria-describedby` to `describedBy` while it is given, and removes it otherwise. The
 * sentence stays where a sighted reader sees it (the bar above the footer); a screen reader now
 * hears it with the button. Drop this when the library's action config takes a description.
 */
export function useDialogActionDescription(
  dialogTestId: string,
  action: "primary" | "secondary" | "tertiary",
  describedBy: string | null,
): void {
  useEffect(() => {
    const selector = `[data-testid="${dialogTestId}-${action}"]`;
    let frame = 0;
    const apply = (): boolean => {
      const button = document.querySelector<HTMLElement>(selector);
      if (!button) return false;
      if (describedBy) button.setAttribute("aria-describedby", describedBy);
      else button.removeAttribute("aria-describedby");
      return true;
    };
    // The dialog's footer is portalled; on the first open it can land a frame after this effect.
    if (!apply())
      frame = requestAnimationFrame(() => {
        apply();
      });
    return () => {
      cancelAnimationFrame(frame);
      document.querySelector<HTMLElement>(selector)?.removeAttribute("aria-describedby");
    };
  }, [dialogTestId, action, describedBy]);
}
